import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { Layout } from '../layout/Layout';

interface AzureRoleProtectedRouteProps {
  children: React.ReactNode;
  requiredRole: string; // Specific Azure AD role like 'TIL.PMP.ADMIN'
  fallbackPath?: string;
}

/**
 * Component to protect routes based on Azure AD roles
 * 
 * This component checks if the user has a specific Azure AD role
 * (such as TIL.PMP.ADMIN or TIL.PMP.USER) directly from the token roles claim.
 * 
 * This performs a direct check against the actual Azure AD role,
 * bypassing the application's internal role mapping system.
 * 
 * @param children The components to render if the user has access
 * @param requiredRole The specific Azure AD role needed for access
 * @param fallbackPath Path to redirect to if the user doesn't have the required role
 */
export const AzureRoleProtectedRoute: React.FC<AzureRoleProtectedRouteProps> = ({
  children,
  requiredRole,
  fallbackPath = '/unauthorized'
}) => {
  const { isAuthenticated, user } = useAuthStore();

  // First check if the user is authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Then check if the user has the required Azure AD role directly from the token
  // This bypasses the application's internal role mapping
  const hasRequiredRole = user?.roles?.includes(requiredRole) || false;
  
  if (!hasRequiredRole) {
    return <Navigate to={fallbackPath} replace />;
  }

  // If authenticated and has the role, render the children
  return <Layout>{children}</Layout>;
}; 